/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import template from './creative-edit-page.component.html';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {setupCreativeEditCtrl} from './creative-edit-setup';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {CreativeRetailProductModalService} from './creative-retail-product-modal.service';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {AdvertiserResource} from '../../components/api/resources/resource/advertiser.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {ExternalDemandSourceResource} from '../../components/api/resources/resource/external-demand-source.resource';
import {LibraryCreativeResource} from '../../components/api/resources/resource/library-creative.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {MarketplaceProductResource} from '../../components/api/resources/resource/marketplace-product.resource';
import {ProductFeedResource} from '../../components/api/resources/resource/product-feed.resource';
import {AdnRollbarService} from '../../components/util/adn-rollbar.service';
import {AssetService} from '../../components/api/resources/resource/asset/asset.service';
import {CreativeHelper} from './creative-helper.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';

import {compareById} from '../common/util/object-id-comparison';

@Component({
  standalone: false,
  selector: 'adn-creative-edit-page',
  template
})
export class CreativeEditPageComponent extends AdminResourceEditPageBase {
  [key: string]: any;

  private _readOnlyDirect: boolean | undefined;
  private _creativeTypeDirect: any;
  private _lineItemInfoDirect: any;
  private setupInitialized = false;

  activeTab: 'design' | 'stats' | 'co2' = 'design';
  previewTab: 'preview' | 'trackers' | 'creativeTag' | 'creativeTagEmail' = 'preview';
  showingCo2 = false;
  showImpsInfo = false;
  showThirdPartyDetails = false;
  showVariables = false;
  readonly codeMirrorOpts = {lineNumbers: true, mode: 'htmlmixed', smartIndent: false};

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  @Input()
  set creativeType(value: any) {
    this._creativeTypeDirect = value;
  }

  get creativeType(): any {
    return this._creativeTypeDirect;
  }

  @Input()
  set lineItemInfo(value: any) {
    this._lineItemInfoDirect = value;
  }

  get lineItemInfo(): any {
    return this._lineItemInfoDirect;
  }

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly $state: StateService,
    private readonly translate: TranslateService,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(ProductFeedResource) private readonly ProductFeed: any,
    @Inject(CreativeHelper) private readonly CreativeHelper: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(AdnRollbarService) private readonly Rollbar: any,
    @Inject(AssetService) private readonly Asset: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LibraryCreativeResource) private readonly LibraryCreative: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(MarketplaceProductResource) private readonly MarketplaceProduct: any,
    @Inject(ExternalDemandSourceResource) private readonly ExternalDemandSource: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdvertiserResource) private readonly Advertiser: any,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly creativeRetailProductModalService: CreativeRetailProductModalService,
    private readonly uiRouter: UIRouter,
    private readonly cdr: ChangeDetectorRef,
    private readonly navigationHistory: NavigationHistoryService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  selectTab(tab: 'design' | 'stats' | 'co2'): void {
    this.activeTab = tab;
    if (tab === 'co2') {
      this.showingCo2 = true;
    }
    if (tab === 'stats') {
      this.showStats = true;
    }
  }

  selectPreviewTab(tab: 'preview' | 'trackers' | 'creativeTag' | 'creativeTagEmail'): void {
    this.previewTab = tab;
    if (tab === 'trackers') {
      this.hasTracker = true;
    }
    if (tab === 'creativeTag') {
      this.hasCreativeTag = true;
    }
    if (tab === 'creativeTagEmail') {
      this.hasCreativeTagEmail = true;
    }
  }

  readonly compareById = compareById;

  trackById(_index: number, item: any): string {
    return item?.id;
  }

  trackByHtmlUrlKey(_index: number, entry: {key: string}): string {
    return entry.key;
  }

  getTextareaRows(comp: any): number {
    const rawRows = comp.maxLength / 75 + 1;
    if (rawRows < 2) {
      return 1;
    }
    if (rawRows > 10) {
      return 10;
    }
    return Math.floor(rawRows);
  }

  protected afterInitEditingModel(): void {
    this.ensureSetup();
    this._creativeAfterInit?.();
  }

  protected afterInitAfterSave(saveObj?: any): void {
    this._creativeAfterInitAfterSave?.(saveObj);
  }

  onAdvertiserMismatchFixed(saved: any): void {
    if (saved) {
      this.editingModel.advertiser = saved.advertiser;
      this.editingModel.validationWarnings = saved.validationWarnings;
    }
    this._creativeAfterInitAfterSave?.(saved);
    this.publishModelUpdate();
  }

  protected applySave(resource: any): void {
    super.applySave(resource);
    this._creativeAfterSave?.(resource);
  }

  protected onBeforeSubmit(): void {
    this._creativeOnBeforeSubmit?.();
  }

  private ensureSetup(): void {
    if (this.setupInitialized || !this.model) {
      return;
    }
    this.setupInitialized = true;

    setupCreativeEditCtrl(this, {
      model: this.model,
      readOnly: this.readOnly,
      creativeType: this.creativeType,
      lineItemInfo: this.lineItemInfo,
      modelComms: this.modelComms,
      $state: this.$state,
      navigationHistory: this.navigationHistory,
      routeParams: this.uiRouter.globals.params,
      translate: this.translate,
      Team: this.Team,
      ProductFeed: this.ProductFeed,
      CreativeHelper: this.CreativeHelper,
      adnListHelper: this.adnListHelper,
      Rollbar: this.Rollbar,
      Asset: this.Asset,
      Creative: this.Creative,
      LibraryCreative: this.LibraryCreative,
      LineItem: this.LineItem,
      MarketplaceProduct: this.MarketplaceProduct,
      ExternalDemandSource: this.ExternalDemandSource,
      LocalNetworkProfile: this.LocalNetworkProfile,
      LocalUserPermissions: this.LocalUserPermissions,
      Advertiser: this.Advertiser,
      TargetingEditorModalService: this.targetingEditorModalService,
      creativeRetailProductModalService: this.creativeRetailProductModalService,
      cdr: this.cdr
    });
  }
}
